import React, {useCallback, useEffect, useState} from 'react'
import {ToastContainer} from 'react-toastify'
import axios from 'axios'
import {Modal, ModalBody, ModalHeader} from 'react-bootstrap'
import {KTCard, KTCardBody, KTIcon} from '../../../../_websoft/helpers'
import LoadingComponent from '../../../components/LoadingComponent/LoadingComponent'
import notifyToast from '../../../components/Toast/Toast'
import {getAuth} from '../../../modules/auth'
import {UposlenikModel} from '../../../models/UposlenikModel'
import {
    getAll,
    operacije,
    pokreniOperaciju,
    zavrsiOperaciju,
} from '../../../api/plaviNalozi'
import {
    getPlaviNalogStatus,
    PlaviNalog,
    PlaviNalogOperacija,
    STATUS_KREIRAN,
    STATUS_U_TOKU,
} from '../../../models/PlaviNalog'

const API_URL = process.env.REACT_APP_API_URL
const auth: UposlenikModel = getAuth()!
const headers = {Accept: 'application/json', Authorization: auth?.access_token ?? ''}

type Masina = {
    Id: number
    Naziv: string
}

const extractRows = (responseData: any): PlaviNalog[] => {
    const paginator = responseData?.data?.data && Array.isArray(responseData.data.data)
        ? responseData.data
        : responseData
    const rows = paginator?.data ?? []

    return Array.isArray(rows) ? rows : []
}

const getArtikalName = (nalog: PlaviNalog) => nalog.artikal?.ArtNaziv ?? nalog.artikal?.Naziv ?? '-'

const getOperacijaName = (item: PlaviNalogOperacija) =>
    item.nazivOperacije ?? item.operacija?.naziv ?? item.naziv ?? '-'

const StatusBadge = ({status}: { status?: number }) => {
    const statusInfo = getPlaviNalogStatus(status)
    return <span className={`badge badge-light-${statusInfo.badge}`}>{statusInfo.label}</span>
}

const PlaviNalogTerminal: React.FC = () => {
    const [nalozi, setNalozi] = useState<PlaviNalog[]>([])
    const [selectedNalog, setSelectedNalog] = useState<PlaviNalog | null>(null)
    const [operacijeData, setOperacijeData] = useState<PlaviNalogOperacija[]>([])
    const [loading, setLoading] = useState(false)
    const [operacijeLoading, setOperacijeLoading] = useState(false)
    const [actionLoadingId, setActionLoadingId] = useState<number | null>(null)
    const [machineModalOpen, setMachineModalOpen] = useState(false)
    const [selectedOperacija, setSelectedOperacija] = useState<PlaviNalogOperacija | null>(null)
    const [masine, setMasine] = useState<Masina[]>([])
    const [selectedMasina, setSelectedMasina] = useState('')
    const [masineLoading, setMasineLoading] = useState(false)
    const [finishModalOpen, setFinishModalOpen] = useState(false)
    const [finishOperacija, setFinishOperacija] = useState<PlaviNalogOperacija | null>(null)
    const [finishKolicina, setFinishKolicina] = useState('')
    const [prijavaKvara, setPrijavaKvara] = useState(false)
    const [opisKvara, setOpisKvara] = useState('')
    const [urgentnaPrijava, setUrgentnaPrijava] = useState('0')

    const loadNalozi = useCallback(async () => {
        setLoading(true)

        try {
            const [kreirani, uToku] = await Promise.all([
                getAll({page: 1, per_page: 100, status: STATUS_KREIRAN}),
                getAll({page: 1, per_page: 100, status: STATUS_U_TOKU}),
            ])
            setNalozi([...extractRows(kreirani.data), ...extractRows(uToku.data)])
        } catch (error: any) {
            notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja naloga.')
        } finally {
            setLoading(false)
        }
    }, [])

    const loadOperacije = useCallback((nalog: PlaviNalog) => {
        setSelectedNalog(nalog)
        setOperacijeLoading(true)

        operacije(nalog.id)
            .then((response) => setOperacijeData(Array.isArray(response) ? response : []))
            .catch((error: any) =>
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja operacija.')
            )
            .finally(() => setOperacijeLoading(false))
    }, [])

    useEffect(() => {
        loadNalozi()
    }, [loadNalozi])

    const refreshSelectedNalog = () => {
        if (selectedNalog) {
            loadOperacije(selectedNalog)
        }
        loadNalozi()
    }

    const openMachineModal = (operacija: PlaviNalogOperacija) => {
        const idOperacije = operacija.idOperacije ?? operacija.operacija?.id

        if (!idOperacije) {
            notifyToast(false, 'Operacija nema validan ID za učitavanje mašina.')
            return
        }

        setSelectedOperacija(operacija)
        setSelectedMasina('')
        setMasine([])
        setMachineModalOpen(true)
        setMasineLoading(true)

        axios
            .get(`${API_URL}/sredstvaZaOperacije`, {headers: headers, params: {operacija: idOperacije}})
            .then((response) => {
                const payload = response.data.map((item) => ({
                    Id: item.Id,
                    Naziv: item.Naziv,
                }))
                setMasine(payload)
            })
            .catch((error: any) =>
                notifyToast(false, error?.response?.data?.message ?? error?.message ?? 'Greška prilikom učitavanja mašina.')
            )
            .finally(() => setMasineLoading(false))
    }

    const onPokreni = () => {
        if (!selectedNalog || !selectedOperacija) {
            return
        }

        if (!selectedMasina) {
            notifyToast(false, 'Odaberite mašinu.')
            return
        }

        setActionLoadingId(selectedOperacija.id)
        pokreniOperaciju(selectedNalog.id, selectedOperacija.id, {idMasine: selectedMasina})
            .then((response) => {
                notifyToast(true, response?.message ?? 'Operacija je pokrenuta.')
                setMachineModalOpen(false)
                setSelectedOperacija(null)
                refreshSelectedNalog()
            })
            .catch((error: any) => notifyToast(false, error?.response?.data?.message ?? error?.message))
            .finally(() => setActionLoadingId(null))
    }

    const openFinishModal = (operacija: PlaviNalogOperacija) => {
        setFinishOperacija(operacija)
        setFinishKolicina('')
        setPrijavaKvara(false)
        setOpisKvara('')
        setUrgentnaPrijava('0')
        setFinishModalOpen(true)
    }

    const onZavrsi = () => {
        if (!selectedNalog || !finishOperacija) {
            return
        }

        const parsedKolicina = Number(finishKolicina)

        if (!Number.isInteger(parsedKolicina) || parsedKolicina <= 0) {
            notifyToast(false, 'Količina mora biti cijeli broj veći od 0.')
            return
        }

        if (prijavaKvara && !opisKvara.trim()) {
            notifyToast(false, 'Unesite opis kvara.')
            return
        }

        setActionLoadingId(finishOperacija.id)
        zavrsiOperaciju(selectedNalog.id, finishOperacija.id, {
            kolicina: parsedKolicina,
            kvar: prijavaKvara ? 1 : 0,
            opisKvara: prijavaKvara ? opisKvara : '',
            urgentnaPrijava: prijavaKvara ? urgentnaPrijava : 0,
        })
            .then((response) => {
                notifyToast(true, response?.message ?? 'Operacija je završena.')
                setFinishModalOpen(false)
                setFinishOperacija(null)
                refreshSelectedNalog()
            })
            .catch((error: any) => notifyToast(false, error?.response?.data?.message ?? error?.message))
            .finally(() => setActionLoadingId(null))
    }

    return (
        <KTCard>
            <ToastContainer />
            <Modal show={machineModalOpen} onHide={() => setMachineModalOpen(false)}>
                <ModalHeader closeButton={true} className='h3'>
                    Odaberi mašinu
                </ModalHeader>
                <ModalBody>
                    <div className='mb-5'>
                        <div className='fw-bold text-gray-800'>{selectedOperacija ? getOperacijaName(selectedOperacija) : ''}</div>
                        <div className='text-muted'>Odaberite mašinu prije pokretanja operacije.</div>
                    </div>
                    {masineLoading ? (
                        <LoadingComponent />
                    ) : (
                        <>
                            <select
                                className='form-select form-select-solid mb-6'
                                value={selectedMasina}
                                onChange={(event) => setSelectedMasina(event.target.value)}
                            >
                                <option value=''>Odaberi mašinu...</option>
                                {masine.map((masina) => (
                                    <option key={masina.Id} value={masina.Id}>
                                        {masina.Naziv}
                                    </option>
                                ))}
                            </select>
                            <div className='d-flex justify-content-end gap-3'>
                                <button type='button' className='btn btn-light' onClick={() => setMachineModalOpen(false)}>
                                    Odustani
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-primary'
                                    disabled={!selectedMasina || actionLoadingId === selectedOperacija?.id}
                                    onClick={onPokreni}
                                >
                                    Pokreni
                                </button>
                            </div>
                        </>
                    )}
                </ModalBody>
            </Modal>
            <Modal show={finishModalOpen} onHide={() => setFinishModalOpen(false)}>
                <ModalHeader closeButton={true} className='h3'>
                    Završi operaciju
                </ModalHeader>
                <ModalBody>
                    <div className='mb-5'>
                        <div className='fw-bold text-gray-800'>{finishOperacija ? getOperacijaName(finishOperacija) : ''}</div>
                        <div className='text-muted'>Unesite završenu količinu i podatke o kvaru ako postoje.</div>
                    </div>
                    <div className='mb-5'>
                        <label className='form-label required fw-bold'>Količina</label>
                        <input
                            type='number'
                            step='1'
                            min='1'
                            className='form-control form-control-solid'
                            value={finishKolicina}
                            onChange={(event) => setFinishKolicina(event.target.value)}
                        />
                    </div>
                    <div className='form-check form-check-custom form-check-solid mb-5'>
                        <input
                            id='plavi-nalog-prijava-kvara'
                            type='checkbox'
                            className='form-check-input'
                            checked={prijavaKvara}
                            onChange={(event) => setPrijavaKvara(event.target.checked)}
                        />
                        <label className='form-check-label fw-bold' htmlFor='plavi-nalog-prijava-kvara'>
                            Prijava kvara
                        </label>
                    </div>
                    {prijavaKvara && (
                        <>
                            <div className='mb-5'>
                                <label className='form-label required fw-bold'>Opis kvara</label>
                                <textarea
                                    rows={4}
                                    className='form-control form-control-solid'
                                    value={opisKvara}
                                    onChange={(event) => setOpisKvara(event.target.value)}
                                />
                            </div>
                            <div className='mb-6'>
                                <label className='form-label fw-bold'>Urgentna prijava kvara</label>
                                <select
                                    className='form-select form-select-solid'
                                    value={urgentnaPrijava}
                                    onChange={(event) => setUrgentnaPrijava(event.target.value)}
                                >
                                    <option value='0'>Ne</option>
                                    <option value='1'>Da</option>
                                </select>
                            </div>
                        </>
                    )}
                    <div className='d-flex justify-content-end gap-3'>
                        <button type='button' className='btn btn-light' onClick={() => setFinishModalOpen(false)}>
                            Cancel
                        </button>
                        <button
                            type='button'
                            className='btn btn-success'
                            disabled={actionLoadingId === finishOperacija?.id}
                            onClick={onZavrsi}
                        >
                            Submit
                        </button>
                    </div>
                </ModalBody>
            </Modal>
            <KTCardBody className='py-6 py-lg-8'>
                <div className='d-flex align-items-start align-items-md-center justify-content-between flex-column flex-md-row gap-3 mb-6'>
                    <div>
                        <h2 className='fw-bold mb-1'>Plavi nalozi</h2>
                        <div className='text-muted'>
                            Odaberite nalog, zatim operaciju koju želite pokrenuti ili završiti.
                        </div>
                    </div>
                    {selectedNalog && (
                        <button type='button' className='btn btn-light-primary' onClick={() => setSelectedNalog(null)}>
                            <KTIcon iconName='arrow-left' className='fs-2' />
                            Nazad na naloge
                        </button>
                    )}
                </div>

                {!selectedNalog && (
                    loading ? (
                        <LoadingComponent />
                    ) : (
                        <div className='row g-5'>
                            {nalozi.length > 0 ? (
                                nalozi.map((nalog) => (
                                    <div className='col-12 col-md-6 col-xl-4' key={nalog.id}>
                                        <button
                                            type='button'
                                            className='card hoverable h-100 border-0 shadow-sm w-100 text-start'
                                            onClick={() => loadOperacije(nalog)}
                                        >
                                            <div className='card-body p-6'>
                                                <div className='d-flex justify-content-between align-items-start gap-3 mb-5'>
                                                    <div>
                                                        <div className='fw-bolder fs-2 text-gray-900'>{nalog.brojNaloga}</div>
                                                        <div className='text-muted fw-semibold'>{getArtikalName(nalog)}</div>
                                                    </div>
                                                    <StatusBadge status={nalog.status} />
                                                </div>
                                                <div className='d-flex justify-content-between align-items-center'>
                                                    <span className='badge badge-light-info fw-bold'>
                                                        Količina: {nalog.kolicina}
                                                    </span>
                                                    <span className='fw-bold text-primary'>
                                                        Otvori <KTIcon iconName='arrow-right' className='fs-2 ms-1' />
                                                    </span>
                                                </div>
                                            </div>
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div className='col-12'>
                                    <div className='text-center text-muted py-10'>Nema aktivnih plavih naloga.</div>
                                </div>
                            )}
                        </div>
                    )
                )}

                {selectedNalog && (
                    <div>
                        <div className='alert alert-primary d-flex flex-column flex-md-row justify-content-between gap-3 mb-6'>
                            <div>
                                <div className='fw-bold fs-4'>{selectedNalog.brojNaloga}</div>
                                <div>{getArtikalName(selectedNalog)}</div>
                            </div>
                            <div className='d-flex align-items-center gap-3'>
                                <StatusBadge status={selectedNalog.status} />
                                <span className='fw-bold'>Količina: {selectedNalog.kolicina}</span>
                            </div>
                        </div>

                        {operacijeLoading ? (
                            <LoadingComponent />
                        ) : (
                            <div className='row g-5'>
                                {operacijeData.length > 0 ? (
                                    operacijeData.map((item) => (
                                        <div className='col-12 col-lg-6' key={item.id}>
                                            <div className='card h-100 border border-gray-300'>
                                                <div className='card-body p-6'>
                                                    <div className='d-flex justify-content-between align-items-start gap-3 mb-5'>
                                                        <div>
                                                            <div className='fw-bolder fs-3 text-gray-900'>
                                                                {getOperacijaName(item)}
                                                            </div>
                                                            <div className='text-muted fw-semibold'>
                                                                Redoslijed: {(item as any).redoslijed ?? '-'}
                                                            </div>
                                                        </div>
                                                        <StatusBadge status={item.status} />
                                                    </div>
                                                    <div className='row g-3 mb-6'>
                                                        <div className='col-6'>
                                                            <div className='text-muted fw-semibold'>Planirano</div>
                                                            <div className='fw-bold'>{item.planiranaKolicina ?? '-'}</div>
                                                        </div>
                                                        <div className='col-6'>
                                                            <div className='text-muted fw-semibold'>Završeno</div>
                                                            <div className='fw-bold'>{item.zavrsenaKolicina ?? '-'}</div>
                                                        </div>
                                                        <div className='col-6'>
                                                            <div className='text-muted fw-semibold'>Norma</div>
                                                            <div className='fw-bold'>{item.norma ?? '-'}</div>
                                                        </div>
                                                        <div className='col-6'>
                                                            <div className='text-muted fw-semibold'>Plan min.</div>
                                                            <div className='fw-bold'>{item.planiranoVrijemeMinuta ?? '-'}</div>
                                                        </div>
                                                    </div>
                                                    <div className='d-flex gap-3'>
                                                        <button
                                                            type='button'
                                                            className='btn btn-primary flex-fill'
                                                            disabled={actionLoadingId === item.id}
                                                            onClick={() => openMachineModal(item)}
                                                        >
                                                            Pokreni
                                                        </button>
                                                        <button
                                                            type='button'
                                                            className='btn btn-success flex-fill'
                                                            disabled={actionLoadingId === item.id}
                                                            onClick={() => openFinishModal(item)}
                                                        >
                                                            Završi
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className='col-12'>
                                        <div className='text-center text-muted py-10'>Nema operacija za odabrani nalog.</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </KTCardBody>
        </KTCard>
    )
}

export default PlaviNalogTerminal
